import {inject, Injectable} from '@angular/core';
import {BookingStatusType, JsonValue, Schema, State, StateAbvs} from "@mindpath/shared";
import {HttpClient} from "@angular/common/http";
import {lastValueFrom} from "rxjs";
import {apiUrl} from "../networking";
import {config} from "../../config";
import * as Sentry from "@sentry/angular";
import {IDontSeeMyPlanNames} from "../statics";

export type DetailedInsurance = Schema<'DetailedInsurance'> & {isCashSelfPay: boolean};


@Injectable({
  providedIn: 'root'
})
export class DataService {

  readonly #cachedInsurances: Map<State, readonly DetailedInsurance[]>;
  #cachedConditions: readonly Schema<'Condition'>[];
  readonly #cachedClinicians: Map<number, ClinicianInfo>;

  constructor() {
    const load = <T>(key: string, defaultVal: T) => {
      const item = sessionStorage.getItem(key);
      if (item) {
        const data = JSON.parse(item);
        if (defaultVal instanceof Map) {
          return new Map(Object.entries(data)) as T;
        }
        return data as T;
      }
      return defaultVal;
    }
    this.#cachedConditions = load('conditions', []);
    this.#cachedInsurances = new Map();
    StateAbvs.forEach(state => {
      const key = `insurances:${state}`;
      const insurances = load<Schema<'DetailedInsurance'>[]>(key, []);
      // Lists cached before plans carried a numeric bookingStatusId are stale; drop them so they refetch.
      if (insurances.some(i => !Array.isArray(i.plans) || i.plans.some(plan => typeof plan.bookingStatusId !== 'number'))) {
        this.#cachedInsurances.set(state, []);
        this.#remove(key);
      } else {
        this.#cachedInsurances.set(state, insurances.map(i => this.#normalizeInsurer(i)));
      }
    });

    this.#cachedClinicians = load('clinicians', new Map());

  }

  #remove(key: string) {
    sessionStorage.removeItem(key);
  }

  #save(key: string, value: unknown) {
    if (value === undefined || value === null) {
      this.#remove(key);
      return;
    }
    sessionStorage.setItem(key, JSON.stringify(value));
  }

  #httpClient = inject(HttpClient);

  removeCachedInsurance(state: State) {
    this.#cachedInsurances.delete(state);
    this.#remove(`insurances:${state}`);
  }

  removeCachedClinician(id: number) {
    this.#cachedClinicians.delete(id);
    this.#save('clinicians', this.#cachedClinicians);
  }

  removeCachedConditions() {
    this.#cachedConditions = [];
    this.#remove('conditions');
  }

  getInsurer(id: number, opt?: { force?: boolean }): Promise<DetailedInsurance> {
    return Sentry.startSpan({
      name: 'Get Insurer By ID',
      op: 'data.getInsurer',
    }, async span => {
      span.setAttribute('insurerId', id.toString());
      let found = StateAbvs.map(s =>
        this.#cachedInsurances.get(s)).map(i =>
        i?.find(i => i.id === id)).filter(i => i)[0];
      if (found && !opt?.force) {
        return found;
      }
      found = this.#normalizeInsurer(await lastValueFrom(this.#httpClient.get<Schema<'DetailedInsurance'>>(apiUrl`${config}/insurers/${id}`)));
      this.#checkPlanFlagOverrides(found);
      return found;
    });
  }

  getInsurancesFor(state: State, opt?: { force?: boolean }): Promise<readonly DetailedInsurance[]> {
    return Sentry.startSpan(
      {
        name: 'Get Insurances For State',
        op: 'data.getInsurancesForState',
      },
      async span => {
        span.setAttribute('state', state);
        let insurances = this.#cachedInsurances.get(state);
        if (insurances && insurances.length && !opt?.force) {
          return insurances;
        }

        insurances = await lastValueFrom(this.#httpClient.get<readonly DetailedInsurance[]>(apiUrl`${config}/insurers?state=${state}`));

        this.#checkPlanFlagOverrides(insurances);
        insurances = insurances.map(x => this.#normalizeInsurer(x));

        this.#cachedInsurances.set(state, insurances);
        this.#save(`insurances:${state}`, insurances);
        return insurances;
      });
  }

  #normalizeInsurer(insurer: Schema<'DetailedInsurance'>): DetailedInsurance {
    const isCashSelfPay = insurer.plans.some(plan => plan.bookingStatusId === BookingStatusType.CashSelfPay);
    return {...insurer, isCashSelfPay};
  }

  #checkPlanFlagOverrides(insurances: Schema<'DetailedInsurance'>| readonly Schema<'DetailedInsurance'>[]) {
    try {
      if (!config.flags?.['dontSeeInsuranceToNewStatus']) {
        return;
      }

      if (!Array.isArray(insurances)) {
        insurances = [insurances as Schema<'DetailedInsurance'>];
      }

      insurances.flatMap(i => i.plans).forEach(plan => {
        if (IDontSeeMyPlanNames.some(name => plan.name.includes(name))) {
          (plan as {bookingStatusId: BookingStatusType}).bookingStatusId = BookingStatusType.PleaseVerifyDelayToInsDetails;
        }
      });
    } catch (e) {
      console.error('Error checking plan flag overrides', e);
    }

  }

  getConditions(opt?: { force?: boolean }): Promise<readonly Schema<'Condition'>[]> {
    return Sentry.startSpan(
      {
        name: 'Get Conditions',
        op: 'data.getConditions',
      },
      async () => {

        let conditions = this.#cachedConditions;
        if (conditions.length && !opt?.force) {
          return conditions;
        }
        conditions = await lastValueFrom(this.#httpClient.get<Schema<'Condition'>[]>(apiUrl`${config}/conditions`));
        this.#cachedConditions = conditions;
        this.#save('conditions', conditions);
        return conditions;
      });
  }

  getClinician(id: number, opt?: { force?: boolean }): Promise<ClinicianInfo> {
    return Sentry.startSpan(
      {
        name: 'Get Clinician By ID',
        op: 'data.getClinician',
      },
      async span => {
        span.setAttribute('clinicianId', id.toString());
        let clinician = this.#cachedClinicians.get(id);
        if (clinician && !opt?.force) {
          return clinician;
        }
        clinician = await lastValueFrom(this.#httpClient.get<ClinicianInfo>(apiUrl`${config}/clinicians/${id}`));
        this.#cachedClinicians.set(id, clinician);
        this.#save('clinicians', this.#cachedClinicians);
        return clinician;
      });
  }

}

export interface ClinicianInfo {
  readonly clinician: Schema<'ClinicianDetailedInfo'>;
  readonly metadata: Record<number, Record<string, JsonValue>>;
}
